"use client";

import Image from "next/image";
import { SPIRIT_RARITY_COLORS, SPIRIT_RARITY_LABELS, SPIRIT_GENRE_LABELS, type SpiritRarity } from "@/lib/types/spirit";
import type { Spirit } from "@/lib/types/spirit";

interface SpiritCardProps {
  spirit: Spirit;
  onClick?: () => void;
}

function SpiritPlaceholder({ rarity }: { rarity: SpiritRarity }) {
  const color = SPIRIT_RARITY_COLORS[rarity] ?? "#a78bfa";
  return (
    <div
      className="flex h-12 w-12 items-center justify-center rounded-full text-xl"
      style={{ backgroundColor: `${color}22`, border: `2px solid ${color}44` }}
    >
      ✨
    </div>
  );
}

function SpiritAvatar({ spirit }: { spirit: Spirit }) {
  if (!spirit.imageUrl) return <SpiritPlaceholder rarity={spirit.rarity} />;

  const isVideo = spirit.avatarType === 'video';

  return (
    <div className="relative h-12 w-12 overflow-hidden rounded-full">
      {isVideo ? (
        <video
          src={spirit.imageUrl}
          loop
          autoPlay
          muted
          playsInline
          className="h-full w-full object-cover"
        />
      ) : (
        <Image
          src={spirit.imageUrl}
          alt={spirit.name}
          fill
          className="object-cover"
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
      )}
      <div className="absolute inset-0 -z-10">
        <SpiritPlaceholder rarity={spirit.rarity} />
      </div>
    </div>
  );
}

export default function SpiritCard({ spirit, onClick }: SpiritCardProps) {
  const color = SPIRIT_RARITY_COLORS[spirit.rarity] ?? "#a78bfa";
  const expPercent = spirit.maxExp > 0 ? (spirit.exp / spirit.maxExp) * 100 : 0;

  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-2xl border border-white/15 bg-[var(--background)]/65 backdrop-blur-sm px-3 py-2.5 text-left transition-all hover:bg-[var(--background)]/80 active:scale-[0.98]"
    >
      <SpiritAvatar spirit={spirit} />

      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-bold text-[var(--foreground)]">{spirit.name}</span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold"
            style={{ backgroundColor: `${color}22`, color }}
          >
            {SPIRIT_RARITY_LABELS[spirit.rarity]}
          </span>
        </div>
        <p className="mt-0.5 text-sm text-[var(--foreground)] opacity-60">
          Lv.{spirit.level}
          {spirit.genre ? `　${SPIRIT_GENRE_LABELS[spirit.genre] ?? spirit.genre}属性` : ""}
        </p>
        <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/20">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{ width: `${expPercent}%`, backgroundColor: color }}
          />
        </div>
        <p className="mt-0.5 text-[10px] text-[var(--foreground)] opacity-50">
          EXP {spirit.exp} / {spirit.maxExp}
        </p>
      </div>
    </button>
  );
}
